import { z } from "zod";

import {
  AgentUserNoteSchema,
  ApplicationAnswerSchema,
  ApplicationSchema,
  ApplicationStatusSchema,
  CandidateProfileSchema,
  OrgSchema,
  ProgramSchema,
  ProgramTypeSchema,
  UserSchema
} from "../models/generated";

/* ------------------------------------------------------------- */
/* Application request / response types                          */
/* ------------------------------------------------------------- */

export enum ApplicationPaymentMethod {
  CREDIT = "CREDIT",
  BANK_TRANSFER = "BANK_TRANSFER"
}

export const ApplicationAnswerInputSchema = z.object({
  questionId: z.string(),
  value: z.string().optional(),
  values: z.array(z.string()).optional()
});
export type ApplicationAnswerInput = z.infer<typeof ApplicationAnswerInputSchema>;

export const CreateApplicationSchema = z.object({
  programId: z.string(),
  paymentMethod: z.nativeEnum(ApplicationPaymentMethod).default(ApplicationPaymentMethod.CREDIT),
  answers: z.array(ApplicationAnswerInputSchema).default([])
});
export type CreateApplication = z.infer<typeof CreateApplicationSchema>;

export const UpdateApplicationStatusSchema = z.object({
  status: ApplicationStatusSchema,
  reviewNotes: z.string().optional()
});
export type UpdateApplicationStatus = z.infer<typeof UpdateApplicationStatusSchema>;

export const RequestSupplementSchema = z.object({
  supplementReq: z.string().min(1)
});
export type RequestSupplement = z.infer<typeof RequestSupplementSchema>;

/* ------------------------------------------------------------- */
/* Detailed response shapes                                       */
/* ------------------------------------------------------------- */

export const ApplicationWithDetailSchema = ApplicationSchema.extend({
  program: ProgramSchema.extend({
    org: OrgSchema.pick({
      id: true,
      name: true,
      slug: true,
      avatar: true
    }).optional(),
    // Flattened content from the active ProgramRevision (nullable if no revision exists)
    title: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    type: ProgramTypeSchema.nullable().optional(),
    country: z.string().nullable().optional(),
    location: z.string().nullable().optional(),
    quota: z.number().int().nullable().optional(),
    requirements: z.string().nullable().optional(),
    benefits: z.string().nullable().optional(),
    costVisible: z.boolean().nullable().optional(),
    depositAmount: z.number().nullable().optional(),
    deadline: z.coerce.date().nullable().optional()
  }),
  candidate: CandidateProfileSchema.extend({
    user: UserSchema.nullable()
  }),
  answers: z.array(
    ApplicationAnswerSchema.extend({
      // question label is not yet pulled to interfaces, keep it generic
    })
  ),
  tx: z
    .object({
      id: z.string(),
      purchaseMeta: z
        .object({
          checkoutUrl: z.string(),
          expiresAt: z.coerce.date(),
          status: z.string(),
          orderCode: z.string()
        })
        .nullable()
    })
    .nullable()
    .optional()
});
export type ApplicationWithDetail = z.infer<typeof ApplicationWithDetailSchema>;

export const CreateApplicationResponseSchema = z.object({
  application: ApplicationWithDetailSchema,
  paymentUrl: z.string().nullable()
});
export type CreateApplicationResponse = z.infer<typeof CreateApplicationResponseSchema>;

/* ------------------------------------------------------------- */
/* Filter schemas for list queries                                */
/* ------------------------------------------------------------- */

export const ListApplicationsQuerySchema = z.object({
  page: z.coerce.number().min(0).default(0),
  limit: z.coerce.number().min(1).default(10),
  status: ApplicationStatusSchema.optional()
});
export type ListApplicationsQuery = z.infer<typeof ListApplicationsQuerySchema>;

/* ------------------------------------------------------------- */
/* Agent note types                                               */
/* ------------------------------------------------------------- */

export const AddAgentNoteSchema = z.object({
  note: z.string().min(1),
  internal: z.boolean().default(false)
});
export type AddAgentNote = z.infer<typeof AddAgentNoteSchema>;

export type AgentUserNoteType = z.infer<typeof AgentUserNoteSchema>;
